import React, { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useServices, Client } from "../../../lib/api";

/**
 * ClientSelector component
 * 
 * Dropdown for selecting a client from the selected instance
 */
interface ClientSelectorProps {
  isTarget?: boolean;
  label?: string;
  serviceId?: number | null;
  selectedClient?: any;
  onClientSelect?: (client: any) => void;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({ 
  isTarget = false,
  label = "Client",
  serviceId,
  selectedClient,
  onClientSelect
}) => {
  const { 
    updateFormField, 
    selectedServiceId, 
    setSelectedServiceId,
    setSelectedClient,
    targetServiceId,
    setTargetServiceId,
    setTargetClient
  } = useWorkflowStore();
  
  // Get the selected instance ID from the form data
  const formData = useWorkflowStore(state => state.formData);
  const instanceId = isTarget ? formData.targetHost : formData.host;
  
  // Use provided serviceId or get from store based on isTarget
  const currentServiceId = serviceId !== undefined 
    ? serviceId 
    : (isTarget ? targetServiceId : selectedServiceId);
  
  // Use provided client setter or get from store based on isTarget
  const setClient = onClientSelect || (isTarget ? setTargetClient : setSelectedClient);
  const setServiceId = isTarget ? setTargetServiceId : setSelectedServiceId;
  
  // Field names based on whether this is for source or target
  const clientField = isTarget ? "targetClient" : "client";
  const clientNameField = isTarget ? "targetClientName" : "clientName";
  const placeholderText = isTarget ? "Select client" : "Select source client";
  
  // Fetch clients for the selected instance
  const { 
    data: clients = [], 
    isLoading: isLoadingClients,
    error: clientsError
  } = useServices(instanceId);
  
  const databaseNameField = isTarget ? "targetDatabaseName" : "databaseName";

  // Auto-select when only 1 client is available
  useEffect(() => {
    if (clients.length === 1 && !currentServiceId) {
      const only = clients[0];
      updateFormField(clientField, only.serviceId.toString());
      setServiceId(only.serviceId.toString());
      updateFormField(clientNameField, only.serviceName);
      updateFormField(databaseNameField, only.databaseName ?? "");
      setClient(only);
    }
  }, [clients, currentServiceId, clientField, clientNameField, databaseNameField, setServiceId, setClient, updateFormField]);

  // Reset selected client when instance changes
  useEffect(() => {
    if (instanceId) {
      setServiceId(null);
      setClient(null);
      updateFormField(clientField, "");
      updateFormField(clientNameField, "");
      updateFormField(databaseNameField, "");
    }
  }, [instanceId, setServiceId, setClient, updateFormField, clientField, clientNameField, databaseNameField]);

  // Handle client selection change
  const handleClientChangeWithDb = (value: string) => {
    if (
      value === "loading-state" ||
      value === "error-state" ||
      value === "no-clients-state"
    ) {
      return;
    }

    updateFormField(clientField, value);
    setServiceId(value);

    const newSelectedClient = clients.find(client => client.serviceId.toString() === value);
    if (newSelectedClient) {
      updateFormField(clientNameField, newSelectedClient.serviceName);
      updateFormField(databaseNameField, newSelectedClient.databaseName ?? "");
      setClient(newSelectedClient);
    }
  };

  // Hide dropdown when there is only 1 client (auto-selected silently)
  if (!isLoadingClients && !clientsError && clients.length <= 1) {
    return null;
  }

  return (
    <div>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor={clientField}>{label}</Label>
        <Select
          value={currentServiceId?.toString() || undefined}
          onValueChange={handleClientChangeWithDb}
        >
          <SelectTrigger id={clientField} className="w-full">
            <SelectValue placeholder={placeholderText} />
          </SelectTrigger>
          <SelectContent>
            {isLoadingClients ? (
              <SelectItem value="loading-state" disabled>
                Loading clients...
              </SelectItem>
            ) : clientsError ? (
              <SelectItem value="error-state" disabled>
                Error: {clientsError instanceof Error ? clientsError.message : "Failed to load clients"}
              </SelectItem>
            ) : clients.length === 0 ? (
              <SelectItem value="no-clients-state" disabled>
                No clients available
              </SelectItem>
            ) : (
              clients.map((client: Client) => (
                <SelectItem key={client.serviceId} value={client.serviceId.toString()}>
                  {client.serviceName}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default ClientSelector;
